import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import apiFetch from "../../utlis/apiFetch"
import { forceLogout } from "../../utlis/forceLogout"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const getAuthToken = () => localStorage.getItem("token")

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout()
  return rejectWithValue(error.message)
}

const normalizeApplications = (value) => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.applications)) return value.applications
  return []
}

export const fetchApplications = createAsyncThunk(
  "applications/fetch",
  async ({ page, limit, filters } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken()
      if (!token) throw new Error("No authentication token found. Please log in.")

      let url = `${API_BASE_URL}/api/application/myApplications`
      const params = new URLSearchParams()
      if (page) params.append('page', page)
      if (limit) params.append('limit', limit)
      // Append any filters as needed
      if (filters && typeof filters === 'object') {
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") params.append(k, v)
        })
      }
      const query = params.toString()
      if (query) url += `?${query}`

      const response = await apiFetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      const data = response._data ?? await response.json()

      if (!response.ok) throw new Error(data.message || "Something went wrong")

      return data.body
    } catch (error) {
      return handleAuthError(error, rejectWithValue)
    }
  },
)

export const createApplication = createAsyncThunk("applications/create", async ({ formData }, { rejectWithValue }) => {
  try {
    const token = getAuthToken()
    if (!token) throw new Error("No authentication token found. Please log in.")

    const response = await apiFetch(`${API_BASE_URL}/api/application/createApplication`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    })

    const data = response._data ?? await response.json()
    if (!response.ok) throw new Error(data.message || "Something went wrong")

    return data
  } catch (error) {
    return handleAuthError(error, rejectWithValue)
  }
})

export const deleteApplication = createAsyncThunk("applications/delete", async (id, { rejectWithValue }) => {
  try {
    const token = getAuthToken()
    if (!token) throw new Error("No authentication token found. Please log in.")

    const response = await apiFetch(`${API_BASE_URL}/api/application/deleteMyApplication/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    const data = response._data ?? await response.json()
    if (!response.ok) throw new Error(data.message || "Something went wrong")

    return id
  } catch (error) {
    return handleAuthError(error, rejectWithValue)
  }
})

export const updateApplication = createAsyncThunk(
  "applications/update",
  async ({ id, formData, isFileUpload, preventLoader = false }, { rejectWithValue }) => {
    try {
      console.log(id)
      console.log(formData)
      console.log(isFileUpload)
      console.log(preventLoader)
      const token = getAuthToken()
      if (!token) throw new Error("No authentication token found. Please log in.")

      const headers = isFileUpload
        ? { Authorization: `Bearer ${token}` }
        : {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      const body = isFileUpload ? formData : JSON.stringify(formData)

      const response = await apiFetch(`${API_BASE_URL}/api/application/updateApplication/${id}`, {
        method: "PUT",
        headers,
        body,
      })

      const data = response._data ?? await response.json()
      if (!response.ok) throw new Error(data.message || "Something went wrong")

      // Return the data and preventLoader flag
      return { data, preventLoader }
    } catch (error) {
      return handleAuthError(error, rejectWithValue)
    }
  },
)

export const deleteFile = createAsyncThunk(
  "applications/deleteFile",
  async ({ applicationId, category, fileId }, { rejectWithValue }) => {
    const formData = new URLSearchParams()
    formData.append("applicationId", applicationId)
    formData.append("category", category)
    formData.append("fileId", fileId)

    try {
      const token = getAuthToken()
      if (!token) throw new Error("No authentication token found. Please log in.")

      const response = await apiFetch(`${API_BASE_URL}/api/application/deleteFile`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const data = response._data ?? await response.json()
      if (!response.ok) throw new Error(data.message || "Something went wrong")

      return data
    } catch (error) {
      return handleAuthError(error, rejectWithValue)
    }
  },
)

export const uploadTransaction = createAsyncThunk(
  "applications/uploadTransaction",
  async ({ applicationId, transactionId, transactionDate, file }, { rejectWithValue }) => {
    try {
      const token = getAuthToken()
      if (!token) throw new Error("No authentication token found. Please log in.")

      const formData = new FormData()
      formData.append("applicationId", applicationId)
      formData.append("transactionId", transactionId)
      formData.append("transactionDate", transactionDate)
      formData.append("other", file)

      const response = await apiFetch(`${API_BASE_URL}/api/application/uploadInvoice`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const data = response._data ?? await response.json()
      if (!response.ok) throw new Error(data.message || "Something went wrong")

      return data.body
    } catch (error) {
      return handleAuthError(error, rejectWithValue)
    }
  },
)

export const updateApplicationNoLoader = createAsyncThunk(
  "applications/updateNoLoader",
  async ({ id, formData, isFileUpload }, { rejectWithValue }) => {
    try {
      const token = getAuthToken()
      if (!token) throw new Error("No authentication token found. Please log in.")

      const headers = isFileUpload
        ? { Authorization: `Bearer ${token}` }
        : {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      const body = isFileUpload ? formData : JSON.stringify(formData)

      const response = await apiFetch(`${API_BASE_URL}/api/application/updateApplication/${id}`, {
        method: "PUT",
        headers,
        body,
      })

      const data = response._data ?? await response.json()
      if (!response.ok) throw new Error(data.message || "Something went wrong")

      // Always pass preventLoader as true
      return { data, preventLoader: true }
    } catch (error) {
      return handleAuthError(error, rejectWithValue)
    }
  },
)

const applicationSlice = createSlice({
  name: "applications",
  initialState: {
    applications: [],
    isLoading: false,
    error: null,
    success: false,
    isDeleting: false,
    totalPages: 1,
    currentPage: 1,
    totalApplications: 0,
  },
  reducers: {
    clearApplications: (state) => {
      state.applications = []
      state.isLoading = false
      state.error = null
      state.success = false
    },
    clearSuccess: (state) => {
      state.success = false // Reset success
    },
  },
  extraReducers: (builder) => {
    builder

      // Add these cases to the extraReducers builder
      .addCase(updateApplicationNoLoader.pending, (state) => {
        // Don't set isLoading to true
        state.success = false
      })
      .addCase(updateApplicationNoLoader.fulfilled, (state, action) => {
        state.success = true
        const updatedApplication = action.payload.data.body
        const applicationsList = normalizeApplications(state.applications)
        const index = applicationsList.findIndex((app) => app._id === updatedApplication._id)

        if (index !== -1) {
          applicationsList[index] = updatedApplication
        }
        state.applications = applicationsList
      })
      .addCase(updateApplicationNoLoader.rejected, (state, action) => {
        state.success = false
        state.error = action.payload
      })

      .addCase(fetchApplications.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.isLoading = false
        // payload may be an array or an object with applications + metadata
        if (Array.isArray(action.payload)) {
          state.applications = normalizeApplications(action.payload)
          state.totalApplications = action.payload.length
          state.totalPages = 1
          state.currentPage = 1
        } else if (action.payload && action.payload.applications) {
          state.applications = normalizeApplications(action.payload.applications)
          state.totalApplications = action.payload.totalApplications || 0
          state.totalPages = action.payload.totalPages || 1
          state.currentPage = action.payload.currentPage || 1
        } else {
          state.applications = normalizeApplications(action.payload)
          state.totalApplications = Array.isArray(action.payload) ? action.payload.length : 0
          state.totalPages = 1
          state.currentPage = 1
        }
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      .addCase(createApplication.pending, (state) => {
        state.isLoading = true
        state.success = false
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.isLoading = false
        state.success = true
        const applicationsList = normalizeApplications(state.applications)
        applicationsList.unshift(action.payload.body.application)
        state.applications = applicationsList
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.isLoading = false
        state.success = false
        state.error = action.payload
      })

      .addCase(deleteApplication.fulfilled, (state, action) => {
        state.isLoading = false
        const applicationsList = normalizeApplications(state.applications)
        state.applications = applicationsList.filter((app) => app._id !== action.payload)
      })
      .addCase(deleteApplication.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      .addCase(updateApplication.pending, (state, action) => {
        // Only set isLoading to true if preventLoader is not true
        if (!action.meta.arg.preventLoader) {
          state.isLoading = true
        }
        state.success = false
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.success = true
        // Only set isLoading to false if preventLoader is not true
        if (!action.meta.arg.preventLoader) {
          state.isLoading = false
        }
        const updatedApplication = action?.payload?.data?.body
        const applicationsList = normalizeApplications(state.applications)
        const index = applicationsList.findIndex((app) => app._id === updatedApplication._id)

        if (index !== -1) {
          applicationsList[index] = updatedApplication
        }
        state.applications = applicationsList
      })
      .addCase(updateApplication.rejected, (state, action) => {
        state.isLoading = false
        state.success = false
        state.error = action.payload
      })

      .addCase(deleteFile.fulfilled, (state, action) => {
        state.isLoading = false
        state.isDeleting = false
        const updatedApplication = action.payload.body

        const applicationsList = normalizeApplications(state.applications)
        const index = applicationsList.findIndex((app) => app._id === updatedApplication._id)
        if (index !== -1) {
          applicationsList[index] = updatedApplication
        }
        state.applications = applicationsList
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.error = action.payload
        state.isDeleting = false
      })
      .addCase(deleteFile.pending, (state, action) => {
        state.isDeleting = true
      })

      .addCase(uploadTransaction.pending, (state) => {
        state.isLoading = true
        state.success = false
      })
      .addCase(uploadTransaction.fulfilled, (state, action) => {
        state.isLoading = false
        state.success = true

        const invoiceData = action.payload.body

        const applicationsList = normalizeApplications(state.applications)
        const index = applicationsList.findIndex((app) => app._id === invoiceData.applicationId)

        if (index !== -1) {
          applicationsList[index] = {
            ...applicationsList[index],
            invoice: invoiceData,
          }
        }
        state.applications = applicationsList
      })

      .addCase(uploadTransaction.rejected, (state, action) => {
        state.isLoading = false
        state.success = false
        state.error = action.payload
      })
  },
})

export const { clearApplications } = applicationSlice.actions
export const { clearSuccess } = applicationSlice.actions
export default applicationSlice.reducer
